import { useMutation } from 'react-query';

import { iFormTokenCheckFields } from '@/components/Form/FormTokenCheck';
import { PAYMENTS_BACKEND_URL } from '@/config';
import axios from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';

import { WRONG_TOKEN, WRONG_TOKEN_VALIDATION } from '..';
import { MobileToken } from '../types';

export type UpdateMobileToken = {
  data: iFormTokenCheckFields;
  groupId: string;
};

export const updateMobileToken = ({ data, groupId }: UpdateMobileToken): Promise<MobileToken> => {
  const endPoint = PAYMENTS_BACKEND_URL + 'mobile-token/check/' + groupId;
  return axios
    .put(
      endPoint,
      { groupId: data.groupId, phone: data.phone, token: data.token },
      {
        validateStatus: (status) => status < 500,
      }
    )
    .then((response: any) => {
      if (typeof response === 'string') {
        throw Error(`Critical error fetching data in ${endPoint}`);
      }

      if (response?.status === 406) {
        return {
          groupId: groupId,
          hasError: WRONG_TOKEN,
        };
      }
      if (response.response == false) {
        return { ...response.data, hasError: WRONG_TOKEN_VALIDATION };
      }
      return response.data;
    })
    .catch((error) => {
      throw Error(`Critical error fetching data in ${endPoint}: ${error}`);
    });
};

type UseUpdateMobileTokenOptions = {
  config?: MutationConfig<typeof updateMobileToken>;
};

export const useUpdateMobileToken = ({ config }: UseUpdateMobileTokenOptions = {}) => {
  return useMutation({
    onMutate: async (creatingNotification: any) => {
      await queryClient.cancelQueries(['mobile-token', creatingNotification]);

      const previousDetails = queryClient.getQueryData<UpdateMobileToken>([
        'mobile-token',
        creatingNotification,
      ]);

      queryClient.setQueryData(['mobile-token', creatingNotification], {
        ...previousDetails,
        ...creatingNotification.data,
      });

      return { previousDetails: previousDetails };
    },
    onError: (_, __, context: any) => {
      if (context?.previousDetails) {
        queryClient.setQueryData(
          ['mobile-token', context.previousDetails],
          context.previousDetails
        );
      }
    },
    onSuccess: (data) => {
      queryClient.refetchQueries(['mobile-token', data]);
    },
    ...config,
    mutationFn: updateMobileToken,
  });
};
